import { useCallback, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import InfiniteScroll from 'react-infinite-scroll-component'
import Table from 'src/features/base/components/Table'
import { ScrollMessage } from 'src/features/base/components/Table/Table.styles'
import { selectProductMovements, selectHasProductMovementsNextPage } from 'src/features/stock-management/StockManagementSeletor'
import { actions } from 'src/features/stock-management/StockManagementState'
import { MovementEnum } from 'src/features/stock-management/StockManagementTypes'
import { InvoiceTypes } from 'src/components/Danfe/danfeTypes'
import { Movement } from 'src/features/types'
import { mapMovementType } from 'src/utils/mappers/mapMovementType'
import { formatDate } from 'src/utils/formatDate'
import columns from './Table.columns'
import { useReactToPrint } from 'react-to-print'
import * as S from './Table.styles'
import { Link } from '@mui/material'

// Tipo da nota (recurso da DANFE) por tipo de movimentação. RECEIVEMENT é a nota de entrada do
// fornecedor (invoice-nfe); as demais, como a NFD da devolução, são NF-e emitidas pela Shopper
// (nfe). Tipo novo com nota entra neste mapa; sem entrada, cai no padrão (nfe)
const invoiceTypeByMovementType: Partial<Record<string, InvoiceTypes>> = {
  [MovementEnum.RECEIVEMENT]: InvoiceTypes.RECEIVEMENT,
  [MovementEnum.RETURN]: InvoiceTypes.RETURN,
}

const getInvoiceType = (movementType: string) => (
  invoiceTypeByMovementType[movementType] ?? InvoiceTypes.RETURN
)

export default () => {
  const movements = useSelector(selectProductMovements)
  const hasNext = useSelector(selectHasProductMovementsNextPage)
  const dispatch = useDispatch()

  const [invoiceHtml, setInvoiceHtml] = useState<string>('')

  const handleNextPage = useCallback(() => {
    dispatch(actions.nextProductMovementsPage())
    dispatch(actions.getProductMovements())
  }, [dispatch])

  const printAreaRef = useRef(null)

  // eslint-disable-next-line react/no-danger
  const showInvoice = () => (<div style={{ pageBreakBefore: 'always' }} dangerouslySetInnerHTML={{ __html: invoiceHtml }} />)

  const printInvoice = useReactToPrint({
    content: () => printAreaRef.current,
    copyStyles: false,
  })

  const setInvoiceToPrint = (invoiceKey: string, type: InvoiceTypes) => {
    setInvoiceHtml('')

    const onSuccess = (_invoiceHtml: string) => {
      setInvoiceHtml(_invoiceHtml)
      printInvoice()
    }

    dispatch(actions.getInvoiceToPrint({ invoiceKey, type, onSuccess }))
  }

  const invoiceHyperlink = (invoice: string | null | undefined, movementType: string) => {
    if (!invoice) return '-'

    return (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <Link
        component='button'
        onClick={() => setInvoiceToPrint(invoice, getInvoiceType(movementType))}
        data-testid='invoice-hyperlink-test-id'
      >
        {invoice}
      </Link>
    )
  }

  const receivementHyperlink = (receivementId: number | null | undefined) => {
    if (!receivementId) return '-'

    return (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <Link
        component='button'
        onClick={() => window.open(`/receivement/${receivementId}/details`, '_blank')}
        data-testid='receivement-hyperlink-test-id'
      >
        {receivementId}
      </Link>
    )
  }

  const prepareMovements = (data: Movement[]) => data.map((m) => (
    {
      ...m,
      createdAt: formatDate(m.createdAt),
      type: mapMovementType(m.type),
      invoice: invoiceHyperlink(m.invoiceKey, m.type),
      receivement: receivementHyperlink(m.receivementId),
    }))

  return (
    <>
      <InfiniteScroll
        dataLength={movements.items.length}
        next={handleNextPage}
        hasMore={hasNext}
        loader={<ScrollMessage>Carregando mais dados</ScrollMessage>}
        endMessage={<ScrollMessage>Fim dos resultados</ScrollMessage>}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <Table
          data={prepareMovements(movements.items)}
          columns={columns}
        />
      </InfiniteScroll>
      <S.PrintArea ref={printAreaRef}>
        {showInvoice()}
      </S.PrintArea>
    </>
  )
}
